// src/components/FAQ.jsx
import React, { useMemo, useState } from "react";

const DATA = [
  {
    id: "global",
    section: "Global",
    items: [
      { q: "What is HashSeal?", a: "A platform to prevent document fraud at the source. We fingerprint the issuer, hash the file, seal it on blockchain, and embed a QR so anyone can verify authenticity." },
      {
        q: "Who can verify a document?",
        a: (
          <div>
            Verification has tiers:
            <ul>
              <li><strong>Tier 0 — Public scan:</strong> Anyone can scan the QR for pass/fail + basic metadata. No account.</li>
              <li><strong>Tier 1 — Registered Verifier:</strong> Free account. Extended metadata, issuer profile, file hash.</li>
              <li><strong>Tier 2 — Accredited Partner:</strong> KYC/KYB. Advanced checks, API/webhooks, audit exports.</li>
              <li><strong>Issuer Admin:</strong> Full telemetry for documents they issued.</li>
            </ul>
          </div>
        )
      },
      { q: "Do recipients need to upload the file?", a: "No if the QR is present. If the QR isn’t available, they can upload the PDF to verify by hash." },
      { q: "Which blockchains do you use?", a: "Permissioned ledger for speed/cost, anchored periodically to a public chain. Anchoring proofs are shown in verification details." },
      { q: "What personal data do you store?", a: "We store the document hash, issuer fingerprint features, and issuer-defined metadata. Raw docs aren’t stored unless secure retention is enabled. GDPR/EU residency available." },
      { q: "Is this an electronic signature?", a: "It complements e-signatures. HashSeal proves integrity and provenance with AI + cryptography; can be combined with eIDAS-qualified signatures if required." }
    ]
  },

  // 🔹 NEW: Sports Events section
  {
    id: "sports",
    section: "Sports Events",
    items: [
      { q: "What race documents are supported?", a: "Finisher diplomas/certificates, result summaries, age-group podium certificates, team/relay certificates, medical fitness notes, and waivers." },
      { q: "Do you issue diplomas?", a: "HashSeal seals each diploma with a cryptographic hash and adds a QR for instant verification." },
      { q: "Is blockchain required for every diploma?", a: "Yes for sealing (to guarantee integrity). We anchor proofs efficiently so costs stay cents-level; personal data stays off-chain (only a hash + event metadata are anchored)." },
      { q: "How do participants verify?", a: "Scan the QR on the diploma. They’ll see integrity status, event name, bib, time, and issuer. If the QR is missing, they can upload the PDF to verify by hash." },
      { q: "How does this integrate with timing/results?", a: "Diploma payloads (name, bib, time, category) are sealed at generation, then exposed via QR on the PDF and a verification link." },
      { q: "Does this work for triathlon, cycling, and swimming?", a: "Yes. The same issuance and verification flow supports running, cycling, triathlon, and swimming — including multi-distance festivals." }
    ]
  },

  {
    id: "insurance",
    section: "Insurance",
    items: [
      { q: "Which insurance docs benefit most?", a: "Invoices/receipts, medical notes, damage reports, proof-of-work, evidence manifests." },
      { q: "How does it reduce fraud?", a: "Edited/reused docs fail verification. Carriers/TPAs can auto-triage via API/webhooks and cut manual reviews." }
    ]
  },
  {
    id: "education",
    section: "Education",
    items: [
      { q: "What academic records are supported?", a: "Diplomas, transcripts, certificates, recommendation letters, admission docs." },
      { q: "How does a recruiter verify?", a: "Scan QR → issuer, program, issuance date, integrity status. Registered recruiters (Tier 1) can view extra permitted fields." }
    ]
  },
  {
    id: "esg",
    section: "ESG / Attestations",
    items: [
      { q: "What ESG docs work well?", a: "Supplier attestations, audit statements, scope data attachments, chain-of-custody records." },
      { q: "Co-signing?", a: "Yes—multiple sign-off parties can attach co-attestations; verification shows each party’s status." }
    ]
  }
];

export default function FAQ({ showTitle = false }) {
  // Flat list for “expand all”
  const allItems = useMemo(() => DATA.flatMap(s => s.items.map(it => `${s.section}:${it.q}`)), []);
  const [expanded, setExpanded] = useState(new Set()); // holds keys of open items

  const expandAll = () => setExpanded(new Set(allItems));
  const collapseAll = () => setExpanded(new Set());

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className="faq">
      {showTitle && <h2 className="section-title">FAQ</h2>}

      {/* Controls */}
      <div className="faq-controls" role="toolbar" aria-label="FAQ controls">
        <button className="btn btn-outline" type="button" onClick={expandAll}>Expand all</button>
        <button className="btn btn-outline" type="button" onClick={collapseAll} style={{ marginLeft: 8 }}>
          Collapse all
        </button>
      </div>

      {/* Sections */}
      {DATA.map(({ id, section, items }) => (
        <section key={id} id={id} className="faq-section-block">
          <h3 className="faq-section-title">{section}</h3>
          {items.map(({ q, a }) => {
            const key = `${section}:${q}`;
            const isOpen = expanded.has(key);
            return (
              <details
                key={key}
                open={isOpen}
                onClick={(e) => {
                  if (e.target.tagName.toLowerCase() === "summary") {
                    e.preventDefault();
                    toggle(key);
                  }
                }}
                className="faq-item"
              >
                <summary aria-expanded={isOpen}>
                  <span className="caret" aria-hidden>▸</span> {q}
                </summary>
                <div className="faq-answer">{a}</div>
              </details>
            );
          })}
        </section>
      ))}
    </div>
  );
}
